"use client";

import React from "react";
import { Calendar, User, Clock, ArrowRight, Share2, Tag } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";



function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="16732" className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-80 animate-pulse rounded-xl bg-muted" data-index={i} />
        ))}
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return null; 
  }

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      {posts.map((post, i) => (
        <Card key={post.id} className="flex h-full flex-col overflow-hidden border-border shadow-sm hover:shadow-lg transition-shadow bg-card rounded-xl" data-index={i}>
          <div className="relative w-full">
            <AspectRatio ratio={16 / 9}>
              {post.featuredImage ? (
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
                  <span className="text-sm">Geen Afbeelding</span>
                </div>
              )}
            </AspectRatio>
          </div>
          <CardHeader className="p-5 pb-2">
            <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground font-sans">
              <Calendar className="h-3 w-3" />
              <span>{post.date ? new Date(post.date).toLocaleDateString("nl-NL") : "Recent"}</span>
            </div>
            <CardTitle className="font-serif text-lg font-bold leading-tight text-secondary line-clamp-2">
              <Link to={post.link || "#"}>{post.title}</Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 pt-2 grow">
            <CardDescription className="line-clamp-3 text-sm text-muted-foreground font-sans">
              {post.excerpt ? (
                <span dangerouslySetInnerHTML={{ __html: post.excerpt }} />
              ) : (
                "Lees meer over deze transformatie en styling tips van onze experts..."
              )}
            </CardDescription>
          </CardContent>
          <CardFooter className="p-5 pt-0">
            <Link to={post.link || "#"} className="w-full">
              <Button variant="outline" className="w-full justify-between group text-secondary border-primary/40 hover:bg-primary hover:text-white rounded-lg font-sans">
                Bekijk Look
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function RelatedPostsWidget() {
  
  
  const relatedQuery = new WP_Query({
    per_page: 3,
    ignore_sticky: true,
  });

  return (
    <div className="mt-16 bg-accent/30 py-12 rounded-2xl">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-8 flex items-center justify-between">
          <h3 className="font-serif text-2xl font-bold text-secondary md:text-3xl">
            Meer Inspiratie
          </h3>
          <Link to="/HomePage">
            <Button variant="ghost" className="text-primary hover:text-primary/80 hover:bg-primary/10 font-sans">
              Bekijk Alles <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
        <PostsProvider wp_query={{ per_page: 3, ignore_sticky: true }}>
          <RelatedPostsConsumer />
        </PostsProvider>
      </div>
    </div>
  );
}



function SinglePostConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-12">
        <div className="mb-8 space-y-4">
          <div className="h-8 w-3/4 animate-pulse rounded bg-muted" />
          <div className="h-4 w-1/2 animate-pulse rounded bg-muted" />
        </div>
        <div className="mb-8 h-96 w-full animate-pulse rounded-xl bg-muted" />
        <div className="space-y-4">
          <div className="h-4 w-full animate-pulse rounded bg-muted" />
          <div className="h-4 w-full animate-pulse rounded bg-muted" />
          <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center text-center">
        <h2 className="mb-4 text-2xl font-serif font-bold text-destructive">Fout bij laden</h2>
        <p className="mb-6 text-muted-foreground font-sans">We konden het bericht dat je zocht niet laden.</p>
        <Button onClick={() => refetch()} className="bg-primary text-white rounded-lg">Probeer Opnieuw</Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center text-center">
        <h2 className="mb-4 text-2xl font-serif font-bold text-secondary">Niet Gevonden</h2>
        <p className="mb-6 text-muted-foreground font-sans">Het bericht dat je zoekt bestaat helaas niet meer.</p>
        <Link to="/HomePage">
          <Button className="bg-primary text-white rounded-lg">Terug naar Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const authorName = post.author?.name || "VIP Company Team";
  const postDate = post.date ? new Date(post.date).toLocaleDateString("nl-NL", {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }) : "";

  return (
    <article className="mx-auto max-w-4xl px-4 py-12 md:py-16">
      {}
      <header className="mb-10 text-center md:text-left">
        <div className="mb-4 flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground md:justify-start font-sans">
          {post.categories && post.categories.length > 0 && (
            <span className="inline-flex items-center rounded-full bg-primary/20 px-3 py-1 text-xs font-medium text-secondary">
              {post.categories[0].name}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            {postDate}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            4 min leestijd
          </span>
        </div>

        <h1 className="mb-6 font-serif text-4xl font-bold leading-tight text-secondary md:text-5xl lg:text-7xl">
          {post.title}
        </h1>

        <div className="flex flex-wrap items-center justify-center gap-6 md:justify-start">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 border border-border">
              <AvatarImage src={post.author?.avatar?.["96"]} alt={authorName} />
              <AvatarFallback className="bg-primary/10 text-primary"><User className="h-5 w-5" /></AvatarFallback>
            </Avatar>
            <div className="text-left">
              <p className="text-sm font-semibold text-secondary font-sans">{authorName}</p>
              <p className="text-xs text-muted-foreground font-sans">Master Stylist</p>
            </div>
          </div>
          
          <div className="hidden h-8 w-px bg-border md:block" />
          
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="h-9 w-9 rounded-full p-0 text-muted-foreground hover:text-primary hover:border-primary border-border">
              <Share2 className="h-4 w-4" />
              <span className="sr-only">Delen</span>
            </Button>
          </div>
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-12 overflow-hidden rounded-xl shadow-lg">
          <AspectRatio ratio={16 / 9}>
            <img
              src={post.featuredImage}
              alt={post.title}
              className="h-full w-full object-cover"
            />
          </AspectRatio>
        </div>
      )}

      {}
      <div className="prose prose-lg prose-headings:font-serif prose-headings:text-secondary prose-a:text-primary prose-a:no-underline hover:prose-a:underline max-w-none text-muted-foreground font-sans">
        {post.content ? (
          <div dangerouslySetInnerHTML={{ __html: post.content }} />
        ) : (
          <p>Er is momenteel geen inhoud beschikbaar voor dit bericht.</p>
        )}
      </div>

      {}
      <div className="mt-12">
        {post.tags && post.tags.length > 0 && (
          <div className="mb-8 flex flex-wrap gap-2">
            {post.tags.map((tag, i) => (
              <div key={tag.id} className="flex items-center gap-1 rounded-md bg-muted px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-primary/20 hover:text-secondary font-sans" data-index={i}>
                <Tag className="h-3 w-3" />
                {tag.name}
              </div>
            ))}
          </div>
        )}
        
        <Separator className="my-8 bg-border" />

        {}
        <div className="rounded-xl bg-card p-6 md:p-8 border border-border shadow-sm">
          <div className="flex flex-col gap-6 md:flex-row md:items-center">
            <Avatar className="h-20 w-20 border-2 border-background shadow-sm">
              <AvatarImage src={post.author?.avatar?.["96"]} alt={authorName} />
              <AvatarFallback className="bg-primary/10 text-primary text-xl font-bold font-serif">
                {authorName.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 text-center md:text-left">
              <h3 className="mb-2 font-serif text-xl font-bold text-secondary">Over {authorName}</h3>
              <p className="text-muted-foreground font-sans">
                Gepassioneerde haarstylist bij VIP Company in Teuge. Toegewijd aan het creëren van meesterwerken en het bieden van persoonlijke aandacht voor elke unieke haarstijl.
              </p>
            </div>
            <div className="flex justify-center md:justify-end">
               <Link to="/HomePage">
                  <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white rounded-lg font-sans">
                    Contact Stylist
                  </Button>
               </Link>
            </div>
          </div>
        </div>
      </div>

      {}
      <RelatedPostsWidget />
    </article>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = wvcClient.getCurrentWPQueryParams();
  
  
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="bg-background w-full">
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}